import { useRef, useEffect } from 'react';
import { Stack, Box } from '@mui/material';
import { CellCoordinates } from '../../../types';
import { GridCell } from './grid-cell';
import {
  GAP_SIZE,
  CELL_HEIGHT,
  CELL_WIDTH,
  BOARD_BOTTOM_PADDING,
  BOARD_RIGHT_PADDING,
  CELL_CARD_GAP,
  CARD_ROWS_SPAN,
  SidebarMode,
} from '../../../config';
import { GridContainer } from './grid-container';
import { useBoardContext } from '../../../hooks/useBoardContext';
import { useNotesContext } from '@/components/tour/notes/hooks/useNotesContext';
import { DraggableCard } from '../../draggable-card';
import { ColumnBoardLabel, ColumnGroupBoardLabel, RowBoardLabel } from './board-labels';
import { useFilterNotes } from '@/components/tour/notes/hooks/useFilterNotes';
import { MappedNoteCard } from '../../../../card';
import { getDayIndex, getTimeSlotIndex } from '../../../utils/card-coordinates';
import { useNotesStore } from '@/components/tour/notes/stores/notes.store';

export function BoardGrid() {
  const {
    moveNoteToBacklog,
    gridData: { days, timeSlots, assignees },
    setSidebarMode,
    toggleBacklogOpen,
    boardSearch,
    boardFilters: { filters },
  } = useBoardContext();
  const { tags, handleMoveToNote, setCurrentAction, setNoteToEdit, setMediaUrls } =
    useNotesContext();
  const topHeaderRef = useRef<HTMLDivElement>(null);
  const leftHeaderRef = useRef<HTMLDivElement>(null);
  const mainContentRef = useRef<HTMLDivElement>(null);

  const notes = useNotesStore((state) => state.notes);
  const selectedNote = useNotesStore((state) => state.selectedNote);
  const { selectNote } = useNotesStore((state) => state.actions);

  const { filteredCardNotes } = useFilterNotes({
    notes,
    filters,
    searchTerm: boardSearch,
    tags,
  });

  useEffect(() => {
    const mainContent = mainContentRef.current;
    const topHeader = topHeaderRef.current;
    const leftHeader = leftHeaderRef.current;

    if (!mainContent || !topHeader || !leftHeader) {
      return null;
    }

    const syncScroll = () => {
      if (topHeader) {
        topHeader.scrollLeft = mainContent.scrollLeft;
      }
      if (leftHeader) {
        leftHeader.scrollTop = mainContent.scrollTop;
      }
    };

    const preventScroll = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    mainContent.addEventListener('scroll', syncScroll);

    topHeader.addEventListener('scroll', preventScroll);
    topHeader.addEventListener('wheel', preventScroll);
    topHeader.addEventListener('touchmove', preventScroll, { passive: false });

    leftHeader.addEventListener('scroll', preventScroll);
    leftHeader.addEventListener('wheel', preventScroll);
    leftHeader.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      mainContent.removeEventListener('scroll', syncScroll);

      topHeader.removeEventListener('scroll', preventScroll);
      topHeader.removeEventListener('wheel', preventScroll);
      topHeader.removeEventListener('touchmove', preventScroll);

      leftHeader.removeEventListener('scroll', preventScroll);
      leftHeader.removeEventListener('wheel', preventScroll);
      leftHeader.removeEventListener('touchmove', preventScroll);
    };
  }, []);

  const calculateColumnGroupWidth = (numColumns: number) => {
    return numColumns * CELL_WIDTH;
  };

  const calculateRowHeight = () => {
    return CELL_HEIGHT;
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `auto 1fr`,
        gridTemplateRows: `auto 1fr`,
        gridTemplateAreas: `
          "corner top"
          "left main"
        `,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        columnGap: '10px',
        rowGap: '8px',
        backgroundColor: '#fff',
        mt: '8px',
        borderTop: '1px solid var(--Gray-300, #D0D5DD)',
        pt: '10px',
      }}
    >
      <Box
        sx={{
          gridArea: 'corner',
          zIndex: 2,
          backgroundColor: 'white',
        }}
      />

      <Box
        ref={topHeaderRef}
        sx={{
          gridArea: 'top',
          overflowX: 'auto',
          overflowY: 'hidden',
          zIndex: 1,
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          pointerEvents: 'none', // Disable pointer events to prevent user interaction
        }}
      >
        <Stack
          direction="row"
          gap={`${GAP_SIZE}px`}
          sx={{ width: 'max-content', minWidth: '100%', pointerEvents: 'none' }}
        >
          {days.map((day) => {
            const groupWidth = calculateColumnGroupWidth(assignees.length);

            return (
              <Stack
                key={day}
                gap="8px"
                sx={{
                  width: groupWidth,
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <ColumnGroupBoardLabel dayTimestamp={day} />
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${assignees.length}, 1fr)`,
                  }}
                >
                  {assignees.map((assignee) => {
                    return (
                      <Box
                        key={assignee.id}
                        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                      >
                        <ColumnBoardLabel assignee={assignee} />
                      </Box>
                    );
                  })}
                </Box>
              </Stack>
            );
          })}

          <Box
            sx={{
              width: '40px',
              minWidth: '40px',
              flexShrink: 0,
              flexGrow: 0,
            }}
          />
        </Stack>
      </Box>

      <Box
        ref={leftHeaderRef}
        sx={{
          gridArea: 'left',
          overflowY: 'auto',
          overflowX: 'hidden',
          zIndex: 1,
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          paddingBottom: `${BOARD_BOTTOM_PADDING}px`,
          pointerEvents: 'none', // Disable pointer events to prevent user interaction
        }}
      >
        <Stack
          sx={{
            height: `${timeSlots.length * CELL_HEIGHT}px`,
            pl: '18px',
            pointerEvents: 'none',
          }}
        >
          {timeSlots.map((timeSlot) => {
            const rowHeight = calculateRowHeight();

            return (
              <Box
                key={timeSlot}
                sx={{
                  height: rowHeight,
                  justifyContent: 'flex-end',
                  width: '100%',
                  display: 'flex',
                  pb: '8px',
                }}
              >
                <RowBoardLabel minutes={timeSlot} />
              </Box>
            );
          })}
        </Stack>
      </Box>

      <Box
        ref={(el: HTMLDivElement) => {
          mainContentRef.current = el;
          // if (gridRef) {
          //   gridRef.current = el;
          // }
        }}
        sx={{
          gridArea: 'main',
          overflow: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          boxSizing: 'border-box',
          paddingBottom: `${BOARD_BOTTOM_PADDING}px`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: `${GAP_SIZE}px`,
            height: `${timeSlots.length * CELL_HEIGHT}px`,
          }}
        >
          {days.map((day, groupIndex) => {
            const groupWidth = calculateColumnGroupWidth(assignees.length);

            return (
              <GridContainer
                key={day}
                columns={assignees.length}
                rows={timeSlots.length}
                sx={{
                  width: groupWidth,
                  flexShrink: 0,
                  height: 'fit-content',
                  position: 'relative',
                }}
              >
                {timeSlots.flatMap((_, rowIndex) => {
                  const cells = assignees.map((_col, colIndex) => {
                    const coordinates: CellCoordinates = {
                      column: colIndex,
                      row: rowIndex,
                      group: groupIndex,
                    };
                    return <GridCell key={JSON.stringify(coordinates)} coordinates={coordinates} />;
                  });
                  return cells;
                })}

                {filteredCardNotes.map((note) => {
                  if (note.backlogEnabled) return null;

                  const dueDate = new Date(note.dueDate);
                  const dayIndex = getDayIndex(dueDate, days);
                  if (dayIndex !== groupIndex) return null;

                  const timeSlotIndex = getTimeSlotIndex(dueDate, timeSlots);
                  const assigneeIndex = assignees.findIndex((a) => a.id === note.assignee);

                  if (assigneeIndex === -1) {
                    console.error(
                      `Could not find assignee for note ${note.id}. Assignee ${note.assignee}`,
                    );
                  }

                  // Early return if timeSlot calculation failed
                  if (timeSlotIndex === -1 || assigneeIndex === -1) {
                    return null;
                  }

                  const topShift = timeSlotIndex * CELL_HEIGHT + CELL_CARD_GAP;
                  const leftShift = assigneeIndex * CELL_WIDTH + CELL_CARD_GAP;

                  return (
                    <Box
                      key={note.id}
                      style={{ position: 'absolute', top: topShift, left: leftShift }}
                    >
                      <DraggableCard id={note.id.toString()} key={note.id}>
                        <MappedNoteCard
                          note={note}
                          indicatorStyle="around"
                          onClick={() => {
                            setSidebarMode(SidebarMode.Note);
                            toggleBacklogOpen(true);
                            selectNote(note.id);
                          }}
                          onMoveToBacklog={() => {
                            moveNoteToBacklog(note.id.toString());

                            if (note.id === selectedNote?.id) {
                              selectNote(null);
                              setSidebarMode(SidebarMode.Backlog);
                            }
                          }}
                          onDelete={() => {
                            setCurrentAction({
                              type: 'delete',
                              note,
                              onSuccess: () => setSidebarMode(SidebarMode.Backlog),
                            });
                          }}
                          onEdit={() => {
                            const message = note.messages.find((m) => m.isMainMessage);
                            if (!message) return;

                            setSidebarMode(SidebarMode.Note);
                            selectNote(note.id);
                            setMediaUrls(
                              message.mediaUrls.map((url) => ({
                                src: url,
                                flaggedToDelete: false,
                              })),
                            );
                            setNoteToEdit({ note, message });
                          }}
                          onStatusChange={() => {
                            setCurrentAction({ type: 'change-status', note });
                          }}
                          onMoveToTagClick={() => handleMoveToNote(note)}
                          sx={{
                            width: CELL_WIDTH - CELL_CARD_GAP * 2,
                            height: CARD_ROWS_SPAN * CELL_HEIGHT - CELL_CARD_GAP * 2,
                          }}
                        />
                      </DraggableCard>
                    </Box>
                  );
                })}
              </GridContainer>
            );
          })}

          <Box
            sx={{
              width: BOARD_RIGHT_PADDING - GAP_SIZE,
              minWidth: BOARD_RIGHT_PADDING - GAP_SIZE,
              flexShrink: 0,
              flexGrow: 0,
              height: '100%',
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
